/**
 * WelcomeTips displays bubbles with message for helping user while he is discovering the application.
 * This class takes as parameter :
 * - editor : main component (the one that that displays pictures, navigation button, etc...)
 * - data : main model
 * - notifications : an array of json object describing all notifications to display
 *  each json object should have those parameters :
 *    - [mandatory] trigger : name of notification. 
 *    - [optionnal] component : graphical component on which bubble will be displayed
 *    - [mandatory] title : title displayed in bubble
 *    - [mandatory] message : message displayed in bubble
 *    - [optionnal] start : if true, this notification will be displayed when app starts (only one notification should have this parameter set to true). 
 *    - [optionnal] width : width of bubble
 *    - [optionnal] height : height of bubble
 * 
 * Displayed message bubbles have a default size (130 x 250). So if message to display is too big/small, you need to provide optionnal parameter in order to resize message bubble.
 * 
 * Here list of possibles notifications (trigger parameter) : 
 * - "first-updates" : notification is triggered after after first updates.
 * - "photo-selected" : notification is triggered when first photo is clicked. For this notification no need to provide a component.
 * - "text-selected" : notification is triggered when first text is clicked. For this notification no need to provide a component.
 * - "background-selected" : notification is triggered when first background is clicked. For this notification no need to provide a component.
 * - "sticker-selected" : notification is triggered when first sticker is clicked. For this notification no need to provide a component.
 * - "area-selected" : notification is triggered when an area is selected for the first time. For this notification no need to provide a component.
 * - "photo-added" : notification is triggered when one or more photos are imported
 * Trigger name must provided even if not in this list. 
 */
fx.service(class WelcomeTips extends fx.DataHandler{

    constructor(data)
    {
        super();

        this._model = data.model.creationModel;
        
        this._editor = data.editor;
        this._user = data.model.account;
        this._numUpdates = 0;
        this._notifications = [];
        this.active = false;
    }

    notifications(notifications)
    {
        for (let i = 0; i < notifications.length; i++)
        {
            let notification = notifications.getItemAt(i);
            if (!this._user.wasNotificated(notification.trigger) || notification.always)
                this._notifications.push(notification);
        }

        if (this._notifications.length > 0)
            this._start(true);
    }

    get helpBubble()
    {
        if (!this._helpBubble)
            this._helpBubble = new shared.components.HelpBubble(this._editor);

        return this._helpBubble;
    }

    get startNotification()
    {
        return this._notifications.find({"start" : true});
    }

    get firstUpdatesNotification()
    {
        return this._notifications.find({"trigger" : "first-updates"});
    }

    get photoSelectedNotification()
    {
        return this._notifications.find({"trigger" : "photo-selected"});
    }

    get photoEmptySelectedNotification()
    {
        return this._notifications.find({"trigger" : "photo-selected-empty"});
    }

    get textSelectedNotification()
    {
        return this._notifications.find({"trigger" : "text-selected"});
    }

    get backgroundSelectedNotification()
    {
       return this._notifications.find({"trigger" : "background-selected" });
    }

    get stickerSelectedNotification()
    {
        return this._notifications.find({"trigger" : "sticker-selected"});
    }

    get areaSelectedNotification()
    {
        return this._notifications.find({"trigger" : "area-selected"});
    }

    get photoAddedNotification()
    {
        return this._notifications.find({"trigger" : "photo-added"});
    }

    get hasElementSelectedNotification()
    {
        return this.photoEmptySelectedNotification || this.photoSelectedNotification || this.textSelectedNotification || this.backgroundSelectedNotification || this.stickerSelectedNotification;
    }

    get currentNotification()
    {
        return this._currentNotification;
    }

    set currentNotification(val)
    {
         if (val && !val.always && this._user.wasNotificated(val.trigger)) return;

        this._currentNotification = val;
        
        this.delay(this._showBubble, val ? (val.start ? 250 : 1500) : null);
    }
    
    onDataEvent(e)
    {
        switch(e.type)
        {
            case "local-photos-selected" :
            case "google-photos-added" :
            case "album-photos-copied" : 
            case "instagram-photos-added" : 
            case "facebook-photos-added" :
                if (this.photoAddedNotification)
                    this.currentNotification = this.photoAddedNotification;
            break;
        }
    }
    onChildUpdated(action)
    {
        this._numUpdates++;

        if (this._numUpdates > 5)
        {
            if (this.firstUpdatesNotification && this.firstUpdatesNotification.component.visible)
                this.currentNotification = this.firstUpdatesNotification;   
//                this.data = null; // so onChildUpdated will no be called anymore
        }
    }

    _start(listen)
    {
        if (this.startNotification)
            this.currentNotification = this.startNotification;
        
        if (this.firstUpdatesNotification || this.photoAddedNotification)
        {
            this.data = this._model; // so onChildUpdated will be called
            this._numUpdates = 0;
        }

        if (listen)
        {
            this.on("current-element-changed", this._onCurrentElementChanged, this._editor);
            this.on("current-area-changed", this._onCurrentAreaChanged, this._editor);
        }

        this.delay(this._setActive, 250);
    }

    _stop()
    {
        // the listeners _start(listen) registered are released here : registering them again, as
        // this did, left one more of each behind on every start / stop cycle
        this.off("current-element-changed", this._onCurrentElementChanged, this._editor);
        this.off("current-area-changed", this._onCurrentAreaChanged, this._editor);
        this.data = null;

        this.delay(this._unsetActive, 250);
    }

    _setActive()
    {
        this.active = true;
    }
    _unsetActive()
    {
        this.active = false;
    }

    _showBubble()
    {
        if (this.currentNotification)
        {
            let popup = fx.dom.find(".Popup");
            
            if (popup && !fx.dom.hasClass(popup, "disabled"))
                return;
            
            let component = this.currentNotification.component;
            if (!component)
                component = this._editor.currentElement;

            if (component && component.initialized)
            {
                if (this.currentNotification.forbidenComponentClass)
                {
                    let forbidenComponent = fx.dom.find("." + this.currentNotification.forbidenComponentClass);
                    if (forbidenComponent && !fx.dom.hasClass(forbidenComponent, "disabled"))
                        return;
                }

                this.helpBubble.show( component, this.currentNotification.title, this.currentNotification.text, this.currentNotification.width, this.currentNotification.height);
                this._user.notificated(this.currentNotification.trigger);
            }

            this._notifications.remove(this.currentNotification);
            if (this._notifications.length == 0)
                this._stop();
        }
    }


    // Edit Photo
    _onCurrentElementChanged()
    {
        if (!this.active) return;

        if (!this.hasElementSelectedNotification) return;

        if (this._editor.currentElement && this._editor.currentElement instanceof shared.stage.components.Photo)
        {
            if (this._editor.currentElement.empty)
                this.currentNotification = this.photoEmptySelectedNotification;
            else
                this.currentNotification = this.photoSelectedNotification;
        }
        else
        if (this._editor.currentElement && this._editor.currentElement instanceof shared.stage.components.Text)
            this.currentNotification = this.textSelectedNotification;
        else
        if (this._editor.currentElement && this._editor.currentElement instanceof shared.stage.components.Background)
            this.currentNotification = this.backgroundSelectedNotification;
        else
        if (this._editor.currentElement && this._editor.currentElement instanceof shared.stage.components.Sticker)
            this.currentNotification = this.stickerSelectedNotification;
        else
            this.currentNotification = null;
    }

    _onCurrentAreaChanged()
    {
        if (!this.active) return;

        if (!this.hasAreaSelectedNotification) return;

        if (this._editor.currentArea )
            this.currentNotification = this.areaSelectedNotification;
    }

});